import { useState } from 'react';

type SpendingData = {
  id: string;
  payableId?: string;
};

type OnClick = (spending: SpendingData) => void;

export type SpendingClickProperty = {
  getIsRowActive: (row: SpendingData) => boolean;
  getOnClick: (rows: SpendingData[]) => OnClick | undefined;
  entityPanelProps: SpendingEntityPanelProps;
};

export type SpendingEntityPanelProps = {
  payableId: string | undefined;
  onClose: () => void;
};

/**
 * Can be extended to support more entity types.
 *
 * This allows to select all the rows related to the same entity.
 */
const getEntityId = (spending: SpendingData) =>
  spending.payableId ?? spending.id;

/**
 * This hook will be extended to support more entity types.
 *
 * For committed spendings: request, invoice, purchase order, subscription, etc.
 */
export const useSpendingClick = (): SpendingClickProperty => {
  const [payableId, setPayableId] = useState<string | undefined>(undefined);
  const [selectedEntityId, setSelectedEntityId] = useState<string | undefined>(
    undefined,
  );

  const onClick = (spending: SpendingData) => {
    if (spending.payableId) {
      setSelectedEntityId(getEntityId(spending));
      setPayableId(spending.payableId);
    }
  };

  const onClose = () => {
    setPayableId(undefined);
    setSelectedEntityId(undefined);
  };

  return {
    getOnClick: (rows: SpendingData[]): OnClick | undefined =>
      rows.some((row) => row.payableId) ? onClick : undefined,
    getIsRowActive: (row: SpendingData) =>
      getEntityId(row) === selectedEntityId,
    entityPanelProps: { payableId, onClose },
  };
};
